import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBatchAuditLogs } from '@/api/audit-logs'
import { FAILURE_REASONS, useBatch, useUpdateBatch, useVoidBatch } from '@/api/batches'
import type { BatchDetail, BatchMovement } from '@/api/batches'
import { useIdempotencyKey } from '@/api/idempotency'
import { useLockVersion } from '@/api/lock-version'
import { useWorkspace } from '@/api/workspace'
import { formatGrams, formatMinutes, selectClass, versionLabel } from '@/components/batches/batch-format'
import { BatchStatusBadge, Figure } from '@/components/batches/batch-parts'
import { CancelBatchDialog } from '@/components/batches/cancel-dialog'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { StaleRecordNotice } from '@/components/stale-record-notice'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// Batch detail (UI-SCREENS F-06, #128, #129, #131, #132): the Recipe snapshot, the slots (Pool,
// est. vs actual grams, or wasted grams once cancelled), once completed the results and the WIP
// item, once cancelled the cancel reason, and the movements (Pool −grams / −value, WIP +parts, and
// a void's reversals linked to the rows they negate). Complete and Cancel follow `can`: Complete
// opens F-07, Cancel the F-08 modal. The Owner voids a completed or cancelled Batch (the dialog
// lists the records to void first when it is refused); the note, and the failure reason while
// parts failed, stay editable (Owner any, Staff own).
export const Route = createFileRoute('/w/$slug/batches/$batchId/')({
  component: BatchDetail,
  staticData: { breadcrumb: 'batches.detail.title' },
})

function BatchDetail() {
  const { t } = useTranslation()
  const { slug, batchId } = Route.useParams()
  const id = Number(batchId)
  const batch = useBatch(slug, id)
  // The Owner-only History section (#137): void, note and failure reason edits, backdate overrides.
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useBatchAuditLogs(slug, id, { enabled: isOwner })
  const [cancelOpen, setCancelOpen] = useState(false)
  const voidBatch = useVoidBatch()
  const updateBatch = useUpdateBatch()
  const idempotency = useIdempotencyKey()

  if (batch.isPending) return <LoadingSkeleton variant="page" />
  if (batch.isError || !batch.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data = batch.data
  const createdBy = data.created_by ? (data.created_by.name ?? data.created_by.email) : '—'

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidBatch.mutate(
      { params: { path: { slug, batch_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateBatch.mutate(
      { params: { path: { slug, id } }, body: { batch: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-3xl flex-col gap-4 ${voidedClass(Boolean(data.voided_at))}`}>
      <Link to="/w/$slug/batches" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('batches.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-xl font-semibold">{data.batch_no}</h1>
        <BatchStatusBadge batch={data} />
        <VoidedBadge record={data} />
        {data.can.void ? (
          <div className="ml-auto">
            <VoidDialog
              onVoid={onVoid}
              pending={voidBatch.isPending}
              errorMessage={voidBatch.isError ? correctionErrorMessage(t, voidBatch.error) : ''}
              onReset={voidBatch.reset}
              blocked={voidBatch.isError ? <DependentRecords slug={slug} error={voidBatch.error} /> : null}
            />
          </div>
        ) : null}
      </div>
      <VoidedNotice record={data} />

      {data.can.complete || data.can.cancel ? (
        <div className="flex flex-wrap gap-2">
          {data.can.complete ? (
            <Button asChild className="h-11">
              <Link to="/w/$slug/batches/$batchId/complete" params={{ slug, batchId }}>
                {t('batches.detail.complete')}
              </Link>
            </Button>
          ) : null}
          {data.can.cancel ? (
            <Button variant="outline" className="h-11" onClick={() => setCancelOpen(true)}>
              {t('batches.detail.cancel')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <CancelBatchDialog slug={slug} batch={data} open={cancelOpen} setOpen={setCancelOpen} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <Figure label={t('batches.columns.machine')} value={data.machine.name} />
        <Figure label={t('batches.columns.modelVersion')} value={versionLabel(t, data)} />
        <Figure label={t('batches.columns.variant')} value={data.variant ? `${data.variant.sku} · ${data.variant.name}` : '—'} />
        <Figure label={t('batches.columns.purpose')} value={t(`batches.purpose.${data.purpose}`)} />
        <Figure label={t('batches.columns.plates')} value={String(data.plates)} />
        <Figure label={t('batches.detail.partsPerPlate')} value={String(data.parts_per_plate)} />
        <Figure label={t('batches.detail.estMinutes')} value={formatMinutes(t, data.est_minutes)} />
        <Figure label={t('batches.columns.date')} value={formatBusinessDate(data.occurred_on)} />
        <Figure label={t('batches.columns.createdBy')} value={createdBy} />
        {data.status === 'cancelled' && data.cancel_reason ? (
          <Figure label={t('batches.detail.cancelReason')} value={t(`batches.cancelReason.${data.cancel_reason}`)} />
        ) : null}
        {data.status === 'cancelled' && data.cancelled_at ? (
          <Figure label={t('batches.detail.cancelledAt')} value={new Date(data.cancelled_at).toLocaleString('id-ID')} />
        ) : null}
      </dl>
      <p className="text-sm text-muted-foreground">{t('batches.detail.snapshotHint')}</p>

      <NoteCard
        note={data.note}
        lockVersion={data.lock_version}
        canEdit={data.can.edit_note}
        pending={updateBatch.isPending}
        errorMessage={updateBatch.isError ? correctionErrorMessage(t, updateBatch.error) : ''}
        onSave={onSaveNote}
        onReset={updateBatch.reset}
      />

      {data.status === 'completed' ? <Results slug={slug} batch={data} /> : null}

      <h2 className="font-heading text-lg font-semibold">{t('batches.detail.slots')}</h2>
      <ul className="flex flex-col gap-3">
        {data.slots.map((slot) => (
          <li key={slot.id}>
            <Card>
              <CardContent className="flex flex-col gap-2 py-4">
                <span className="font-medium">{slot.label}</span>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <Figure label={t('batches.detail.pool')} value={slot.pool.label} />
                  <Figure label={t('batches.detail.estGrams')} value={formatGrams(slot.est_grams)} />
                  {slot.actual_grams !== null ? (
                    <Figure
                      label={t(data.status === 'cancelled' ? 'batches.detail.wastedGrams' : 'batches.detail.actualGrams')}
                      value={formatGrams(slot.actual_grams)}
                    />
                  ) : null}
                </dl>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      {data.wip ? <WipCard wip={data.wip} /> : null}

      {data.movements.length > 0 ? (
        <>
          <h2 className="font-heading text-lg font-semibold">{t('batches.detail.movements')}</h2>
          <Card>
            <CardContent className="py-4">
              <ul className="flex flex-col divide-y">
                {data.movements.map((movement) => (
                  <li key={movement.id} className="py-3 first:pt-0 last:pb-0">
                    <MovementRow movement={movement} />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      ) : null}

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// OK / failed / printed, the failure reason and the actual time (#126 Q8). The failure reason
// stays editable while parts failed (#126 Q11), by whoever may edit the note, and is frozen once
// the Batch is voided.
function Results({ slug, batch }: { slug: string; batch: BatchDetail }) {
  const { t } = useTranslation()
  const canEditReason = batch.can.edit_note && (batch.parts_failed ?? 0) > 0 && !batch.voided_at

  return (
    <>
      <h2 className="font-heading text-lg font-semibold">{t('batches.detail.results')}</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <Figure label={t('batches.detail.partsOk')} value={formatQty(batch.parts_ok ?? 0)} />
        <Figure label={t('batches.detail.partsFailed')} value={formatQty(batch.parts_failed ?? 0)} />
        <Figure label={t('batches.detail.partsPrinted')} value={formatQty(batch.parts_printed ?? 0)} />
        <Figure
          label={t('batches.detail.failureReason')}
          value={batch.failure_reason ? t(`batches.failureReason.${batch.failure_reason}`) : '—'}
        />
        <Figure
          label={t('batches.detail.actualMinutes')}
          value={batch.actual_minutes === null ? '—' : formatMinutes(t, batch.actual_minutes)}
        />
        <Figure
          label={t(batch.entry_mode === 'after_the_fact' ? 'batches.detail.recordedAt' : 'batches.detail.completedAt')}
          value={batch.completed_at ? new Date(batch.completed_at).toLocaleString('id-ID') : '—'}
        />
      </dl>
      {canEditReason ? <FailureReasonEditor slug={slug} batch={batch} /> : null}
    </>
  )
}

// The failure reason from the fixed list, saved with the Batch's `lock_version`; audited by the
// server as `failure_reason_edit`.
function FailureReasonEditor({ slug, batch }: { slug: string; batch: BatchDetail }) {
  const { t } = useTranslation()
  const updateBatch = useUpdateBatch()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(batch.failure_reason ?? '')
  const version = useLockVersion(batch.lock_version)

  // Back to the Batch as it is now saved, following its live lock_version again (#142).
  function restart() {
    setValue(batch.failure_reason ?? '')
    version.reload()
    updateBatch.reset()
  }

  function stopEditing() {
    setEditing(false)
    restart()
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    updateBatch.mutate(
      {
        params: { path: { slug, id: batch.id } },
        body: { batch: { failure_reason: value, lock_version: version.lockVersion } },
      },
      { onSuccess: () => setEditing(false), onSettled: (_data, error) => version.settle(error) },
    )
  }

  if (!editing) {
    return (
      <Button
        type="button"
        variant="outline"
        className="h-11 self-start"
        onClick={() => {
          restart()
          setEditing(true)
        }}
      >
        {t('batches.detail.editFailureReason')}
      </Button>
    )
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      {version.stale ? (
        <StaleRecordNotice onReload={restart} />
      ) : (
        <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
          {updateBatch.isError ? correctionErrorMessage(t, updateBatch.error) : ''}
        </p>
      )}
      <select
        aria-label={t('batches.detail.failureReason')}
        className={selectClass}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      >
        {FAILURE_REASONS.map((reason) => (
          <option key={reason} value={reason}>
            {t(`batches.failureReason.${reason}`)}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={updateBatch.isPending}>
          {updateBatch.isPending ? t('corrections.saving') : t('corrections.save')}
        </Button>
        <Button type="button" variant="outline" className="h-11" onClick={stopEditing}>
          {t('corrections.cancel')}
        </Button>
      </div>
    </form>
  )
}

// The WIP item the OK parts went to: the Model and its colors per slot (production only).
function WipCard({ wip }: { wip: NonNullable<BatchDetail['wip']> }) {
  const { t } = useTranslation()

  return (
    <>
      <h2 className="font-heading text-lg font-semibold">{t('batches.detail.wip')}</h2>
      <Card>
        <CardContent className="flex flex-col gap-2 py-4">
          <span className="font-medium">{wip.model.name}</span>
          <ul className="text-sm text-muted-foreground">
            {wip.colors.map((color) => (
              <li key={color.model_slot_id}>
                {color.label}: {color.pool.label}
              </li>
            ))}
          </ul>
          <dl className="text-sm">
            <Figure label={t('batches.detail.wipQtyAdded')} value={signed(wip.qty_added, formatQty)} />
          </dl>
        </CardContent>
      </Card>
    </>
  )
}

// A Pool deduction (−grams / −value) or the WIP parts added (+parts, no value). A void adds the
// exact negation of each row, linked to it by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ movement }: { movement: BatchMovement }) {
  const { t } = useTranslation()
  const unit = movement.pool ? ` ${movement.pool.unit}` : ''

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{movement.pool ? movement.pool.label : t('batches.detail.movementWip')}</span>
        {movement.reverses_movement_id ? (
          <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Figure label={t('batches.detail.qtyDelta')} value={`${signed(movement.qty_delta, formatQty)}${unit}`} />
        <Figure
          label={t('batches.detail.valueDelta')}
          value={movement.value_delta === null ? '—' : `Rp ${signed(movement.value_delta, formatMoney)}`}
        />
      </dl>
    </div>
  )
}

const signed = (value: number, format: (value: number) => string) =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${format(Math.abs(value))}`
